<template>
  <div class="card-modern">
  
    <!-- HEADER -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div>
        <h3 class="card-title-modern">Monitoring Document H-60 Expired</h3>
        <small class="text-muted">
          Daftar dokumen yang akan expired dalam
          <strong>{{ filters.days }}</strong> hari ke depan.
        </small>
      </div>
    </div>
  
    <!-- FILTER + EXPORT -->
    <div class="d-flex align-items-end justify-content-end gap-2 mb-3 flex-wrap">
      <button class="btn btn-sm bg-success-subtle border border-success-subtle text-success-emphasis"
        style="border-radius: 8px;" :disabled="exporting" @click="exportExcel">
        <span v-if="exporting">
          <span class="spinner-border spinner-border-sm me-1"></span>Mengunduh...
        </span>
        <span v-else>
          <i class="bi bi-file-earmark-excel me-1"></i>Export Excel
        </span>
      </button>
  
      <div style="width: 120px;">
        <label class="form-label small text-muted mb-1">Hari (H-)</label>
        <input type="number" min="1" max="365" v-model.number="filters.days" class="form-control"
          style="border-radius: 8px;" />
      </div>
  
      <!-- Filter Departemen: hanya untuk Administrator -->
      <div v-if="isAdmin" style="width: 220px;">
        <label class="form-label small text-muted mb-1">Departemen</label>
        <select v-model="filters.division_id" class="form-select" style="border-radius: 8px;">
          <option value="">Semua Departemen</option>
          <option v-for="d in divisions" :key="d.mst_division_id" :value="d.mst_division_id">
            {{ d.mst_division_name }}
          </option>
        </select>
      </div>
  
      <button class="button-modern" @click="fetchData">
        <i class="bi bi-search me-1"></i>Cari
      </button>
      <button class="btn btn-sm bg-light border text-dark" style="border-radius: 8px; height: 38px;" @click="resetFilter">
        Reset
      </button>
    </div>
  
    <!-- ALERT -->
    <div v-if="!loading && data.length > 0" class="alert-box mb-3">
      <strong>⚠️ {{ data.length }} item akan expired dalam {{ filters.days }} hari ke depan.</strong>
      <span v-if="!isAdmin && userDivisionName" class="ms-2 text-muted">
        (Departemen: {{ userDivisionName }})
      </span>
    </div>
  
    <!-- TABLE -->
    <div class="modern-table-wrapper">
      <table class="table-modern">
        <thead>
          <tr>
            <th style="width: 40px;">No</th>
            <th>Tipe</th>
            <th>Nomor Dokumen</th>
            <th>Perusahaan</th>
            <th>Tipe Perpanjangan</th>
            <th>Tanggal Berakhir</th>
            <th class="text-center">Sisa Hari</th>
            <th class="text-center">Aksi</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="8" class="text-center py-4">
              <span class="spinner-border spinner-border-sm me-2"></span>
              Memuat data...
            </td>
          </tr>
          <tr v-else-if="data.length === 0">
            <td colspan="8" class="text-center py-4 text-muted">
              Tidak ada data yang akan expired dalam {{ filters.days }} hari.
            </td>
          </tr>
          <tr v-for="(item, idx) in data" :key="idx" v-else>
            <td>{{ idx + 1 }}</td>
            <td>
              <span :class="getSourceBadgeClass(item.source_type)">
                {{ getSourceLabel(item.source_type) }}
              </span>
            </td>
            <td class="fw-bold">{{ item.reference_number || '-' }}</td>
            <td>{{ item.company_name || '-' }}</td>
            <td>{{ item.renewal_type_name || '-' }}</td>
            <td>{{ formatDate(item.end_date) }}</td>
            <td class="text-center">
              <span :class="getDayClass(item.days_until_expired)">
                H{{ item.days_until_expired < 0 ? '+' : '-' }}{{ Math.abs(item.days_until_expired) }} </span>
            </td>
            <td class="text-center">
              <button class="btn-icon-hover icon-primary" @click="openItem(item)" title="Lihat Detail">
                <i class="bi bi-eye"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import reportExpiringService from '@/services/reportExpiringService';
import divisionService from '@/services/divisionService';
import { getUserLogin } from '@/utils/auth';

const router = useRouter();
const user = getUserLogin() || {};

const isAdmin = computed(() => {
  const name = (user.group_name || '').toLowerCase();
  return name.includes('admin');
});

const userDivisionName = computed(() => user.mst_division_name || '');

const data = ref([]);
const loading = ref(false);
const exporting = ref(false);
const divisions = ref([]);

const filters = reactive({
  days: 60,
  division_id: '',
});

const buildParams = () => {
  const params = { days: filters.days || 60 };
  if (isAdmin.value && filters.division_id !== '' && filters.division_id != null) {
    params.division_id = filters.division_id;
  }
  return params;
};

const fetchData = async () => {
  loading.value = true;
  try {
    const res = await reportExpiringService.getExpiring(buildParams());
    data.value = res.data.data || [];
  } catch (err) {
    console.error('Gagal mengambil data Monitoring H-60', err);
    alert('Gagal mengambil data Monitoring Document H-60 Expired');
    data.value = [];
  } finally {
    loading.value = false;
  }
};

const loadDivisions = async () => {
  if (!isAdmin.value) return;
  try {
    const res = await divisionService.getAll();
    divisions.value = res.data?.data || [];
  } catch (e) {
    console.error('Gagal load division', e);
    divisions.value = [];
  }
};

const resetFilter = () => {
  filters.days = 60;
  filters.division_id = '';
  fetchData();
};

const exportExcel = async () => {
  exporting.value = true;
  try {
    const res = await reportExpiringService.exportExcel(buildParams());
    const blob = new Blob([res.data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Monitoring_Document_H60_Expired.xlsx`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error('Gagal export Excel', err);
    alert('Gagal export Excel');
  } finally {
    exporting.value = false;
  }
};

const formatDate = (date) => {
  if (!date) return '-';
  // Selalu ambil bagian tanggal (yyyy-mm-dd) dulu — hindari shift UTC→WIB
  const s = String(date);
  const m = s.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return new Intl.DateTimeFormat('id-ID', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    }).format(d);
  }
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '-';
  // Fallback: pakai UTC date parts (bukan local) supaya tidak +1 hari
  const d2 = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  return new Intl.DateTimeFormat('id-ID', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(d2);
};

const getDayClass = (days) => {
  if (days === null || days === undefined) return '';
  if (days <= 30) return 'text-danger fw-bold';
  if (days <= 60) return 'text-warning fw-bold';
  return 'text-success';
};

/** Normalisasi source_type (kode lama ATAU nama keterangan dari SP) */
const normType = (type) => String(type || '').toUpperCase().trim();

const isDocumentType = (type) => {
  const t = normType(type);
  return (
    t === 'DOCUMENT' ||
    t.includes('SURAT PERINTAH') ||
    t.includes('PERJANJIAN') ||
    t.includes('SPK') ||
    t.includes('PKS') ||
    t.includes('ADENDUM') ||
    t.includes('ADDENDUM')
  );
};

const isTransportType = (type) => {
  const t = normType(type);
  return t === 'TRANSPORT_OFFER' || t.includes('PENAWARAN');
};

const isAssetType = (type) => {
  const t = normType(type);
  return (
    t === 'ASET_IT' ||
    t === 'ASSET' ||
    t === 'INFRASTRUCTURE' ||
    t.includes('ASET') ||
    t.includes('ASSET') ||
    t.includes('INFRA')
  );
};

const getSourceLabel = (type) => {
  if (isDocumentType(type)) {
    // Tampilkan nama dari API kalau sudah deskriptif
    const raw = String(type || '');
    if (raw && raw !== 'DOCUMENT') return raw;
    return 'Dokumen';
  }
  if (isTransportType(type)) return 'Penawaran Harga';
  if (isAssetType(type)) return 'Infrastruktur IT';
  return type || '-';
};

const getSourceBadgeClass = (type) => {
  if (isDocumentType(type)) return 'badge-primary';
  if (isTransportType(type)) return 'badge-warning';
  if (isAssetType(type)) return 'badge-info';
  return 'badge-secondary';
};

const openItem = (item) => {
  const from = router.currentRoute.value.fullPath;
  const id = item.reference_id;

  if (isDocumentType(item.source_type)) {
    router.push({
      name: 'detaildocument',
      params: { id },
      query: { from },
    });
  } else if (isTransportType(item.source_type)) {
    router.push({
      name: 'transportoffersdetail',
      params: { id },
      query: { from },
    });
  } else if (isAssetType(item.source_type)) {
    router.push({
      name: 'detailInfrastructure',
      params: { id },
      query: { from },
    });
  } else {
    // fallback aman: anggap dokumen (bukan infra)
    console.warn('[report-expiring] unknown source_type:', item.source_type);
    router.push({
      name: 'detaildocument',
      params: { id },
      query: { from },
    });
  }
};

onMounted(async () => {
  await loadDivisions();
  await fetchData();
});
</script>

<style scoped>
.alert-box {
  background: #fff3cd;
  border: 1px solid #ffecb5;
  color: #664d03;
  padding: 0.75rem 1rem;
  border-radius: 8px;
}
</style>